'use client';

import { Card } from "@/components/ui/card";
import { Smartphone, ArrowRight, Youtube, Info, ShoppingBag, SlidersHorizontal } from "lucide-react";
import Link from "next/link";
import { useState, useEffect } from "react";

export default function HelpPage() {
  const [isMounted, setIsMounted] = useState(false);

  // Set isMounted to true after hydration
  useEffect(() => {
    setIsMounted(true);
  }, []);

  // Skeleton loader during hydration
  if (!isMounted) {
    return (
      <div className="container max-w-2xl mx-auto p-4 pb-32 space-y-6">
        <div className="h-8 w-40 bg-gray-200 dark:bg-gray-700 animate-pulse rounded-md mx-auto mb-8"></div>

        {/* Video Card Skeleton */}
        <div className="p-4 space-y-3 bg-card rounded-lg border border-border">
          <div className="flex items-center gap-2">
            <div className="h-5 w-5 bg-gray-200 dark:bg-gray-700 animate-pulse rounded-md"></div>
            <div className="h-6 w-48 bg-gray-200 dark:bg-gray-700 animate-pulse rounded-md"></div>
          </div>
          <div className="h-4 w-full bg-gray-200 dark:bg-gray-700 animate-pulse rounded-md"></div>
          <div className="relative w-full pt-[56.25%] bg-gray-200 dark:bg-gray-700 animate-pulse rounded-md"></div>
        </div>

        {/* Installation Card Skeleton */}
        <div className="p-4 space-y-3 bg-card rounded-lg border border-border">
          <div className="flex items-center gap-2">
            <div className="h-5 w-5 bg-gray-200 dark:bg-gray-700 animate-pulse rounded-md"></div>
            <div className="h-6 w-40 bg-gray-200 dark:bg-gray-700 animate-pulse rounded-md"></div>
          </div>
          <div className="h-4 w-full bg-gray-200 dark:bg-gray-700 animate-pulse rounded-md"></div>
          <div className="h-4 w-24 bg-gray-200 dark:bg-gray-700 animate-pulse rounded-md mt-1"></div>
        </div>

        {/* Location Card Skeleton */}
        <div className="p-4 space-y-3 bg-card rounded-lg border border-border">
          <div className="flex items-center gap-2">
            <div className="h-5 w-5 bg-gray-200 dark:bg-gray-700 animate-pulse rounded-md"></div>
            <div className="h-6 w-40 bg-gray-200 dark:bg-gray-700 animate-pulse rounded-md"></div>
          </div>
          <div className="h-4 w-full bg-gray-200 dark:bg-gray-700 animate-pulse rounded-md"></div>
          <div className="h-4 w-24 bg-gray-200 dark:bg-gray-700 animate-pulse rounded-md mt-1"></div>
        </div>

        {/* Three more card skeletons for the remaining sections */}
        {[1, 2, 3].map((i) => (
          <div key={i} className="p-4 space-y-3 bg-card rounded-lg border border-border">
            <div className="flex items-center gap-2">
              <div className="h-5 w-5 bg-gray-200 dark:bg-gray-700 animate-pulse rounded-md"></div>
              <div className="h-6 w-36 bg-gray-200 dark:bg-gray-700 animate-pulse rounded-md"></div>
            </div>
            <div className="h-4 w-full bg-gray-200 dark:bg-gray-700 animate-pulse rounded-md"></div>
            <div className="h-4 w-3/4 bg-gray-200 dark:bg-gray-700 animate-pulse rounded-md"></div>
          </div>
        ))}
      </div>
    );
  }

  // Recommended articles
  const recommendedArticles = [
    {
      title: "What is BatchFinder?",
      description: "Learn about BatchFinder and its key features",
      href: "/help/what-is-batchfinder",
      icon: Info,
    },
    {
      title: "How do I start?",
      description: "Step-by-step instructions for getting started with BatchFinder",
      href: "/help/how-do-i-start",
      icon: ShoppingBag,
    },
    {
      title: "How do filters work?",
      description: "Understand how Filter Profiles work and see examples",
      href: "/help/how-do-filters-work",
      icon: SlidersHorizontal,
    },
    {
      title: "Setup Video",
      description: "Watch our complete setup guide and see proof of BatchFinder in action",
      href: "/help/video",
      icon: Youtube,
    },
    {
      title: "Verification Help",
      description: "Learn how to verify your account and avoid issues with selfies",
      href: "/help/verification",
      icon: Smartphone,
    },
  ];

  // All articles ordered by importance (non-recommended first, then recommended articles)
  const allArticles = [
    {
      title: "How to set your location",
      description: "Learn how to set your location and use the map with the complete legend guide",
      href: "/help/how-to-set-your-location",
    },
    {
      title: "App Installation",
      description: "Learn how to install this app on your home screen for quick access",
      href: "/help/install",
    },
    {
      title: "Location Settings",
      description: "Enable location access in your browser for this app to work properly",
      href: "/help/location",
    },
    // Recommended articles (users have already seen these, so placed last)
    {
      title: "What is BatchFinder?",
      description: "Learn about BatchFinder and its key features",
      href: "/help/what-is-batchfinder",
    },
    {
      title: "How do I start?",
      description: "Step-by-step instructions for getting started with BatchFinder",
      href: "/help/how-do-i-start",
    },
    {
      title: "How do filters work?",
      description: "Understand how Filter Profiles work and see examples",
      href: "/help/how-do-filters-work",
    },
    {
      title: "Setup Video",
      description: "Watch our complete setup guide and see proof of BatchFinder in action",
      href: "/help/video",
    },
    {
      title: "Verification Help",
      description: "Learn how to verify your account and avoid issues with selfies",
      href: "/help/verification",
    },
  ];

  // Main content after hydration
  return (
    <div className="container max-w-2xl mx-auto p-4 pb-32 space-y-6">
      <h1 className="text-2xl font-bold text-center mb-8 text-primary">Help</h1>

      {/* Recommended Section */}
      <div className="space-y-4">
        <h2 className="text-lg font-semibold">Recommended</h2>
        <div className="space-y-3">
          {recommendedArticles.map((article, index) => {
            const Icon = article.icon;
            return (
              <Link key={index} href={article.href} className="block">
                <Card className="p-4 space-y-2 hover:shadow-md transition-shadow">
                  <div className="flex items-center gap-2">
                    <Icon className="h-5 w-5 text-primary" />
                    <h3 className="font-semibold">{article.title}</h3>
                    <ArrowRight className="h-4 w-4 ml-auto text-muted-foreground" />
                  </div>
                  <p className="text-sm text-muted-foreground">{article.description}</p>
                </Card>
              </Link>
            );
          })}
        </div>
      </div>

      {/* Featured In Section */}
      <Card className="p-4 space-y-3 hover:shadow-md transition-shadow bg-gradient-to-r from-amber-50 to-amber-100 dark:from-amber-900/20 dark:to-amber-800/20 border-amber-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-lg font-semibold">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5 text-amber-600">
              <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
            </svg>
            <h2>Featured in Ridester</h2>
          </div>
          <a
            href="https://www.ridester.com/instacart-batch-grabber/"
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs px-2 py-1 bg-amber-600 text-white rounded-md hover:bg-amber-700 transition-colors"
          >
            Read Article
          </a>
        </div>
        <p className="text-muted-foreground text-sm">
          BatchFinder has been recognized as the <span className="font-semibold">#1 recommended batch grabber</span> in Ridester's 2025 guide. They praised our tool for being <span className="italic">"compatible with both Apple and Android devices"</span> and helping users <span className="italic">"save an average of 3 hours daily and see an earnings surge of 30-40%."</span>
        </p>
      </Card>

      {/* All Articles Section */}
      <div className="space-y-4">
        <h2 className="text-lg font-semibold">All Articles</h2>
        <div className="space-y-3">
          {allArticles.map((article, index) => (
            <Link key={index} href={article.href} className="block">
              <Card className="px-4 py-3 hover:shadow-md transition-shadow w-full">
                <div className="flex items-center gap-2">
                  <h4 className="font-medium">{article.title}</h4>
                  <ArrowRight className="h-4 w-4 ml-auto text-muted-foreground" />
                </div>
                <p className="text-sm text-muted-foreground mt-1">{article.description}</p>
              </Card>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}
